import {
  Button,
  DecimalInput,
  Dialog,
  Label,
  MoneyInput,
  SelectField,
  TextField,
} from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import type { TransactionDto } from "../dto.js";

/**
 * Record a buy or a sell, or correct one.
 *
 * ==========================================================================================
 * THE DATE INPUT IS CAPPED AT TODAY AND HAS NO FLOOR
 * ==========================================================================================
 *
 * Backdating is a REQUIREMENT — a household adopting GLADIS in 2026 has to be able to record what it
 * bought in 2019, or every average cost is wrong. Future dating is refused, because the portfolio
 * records what has happened.
 *
 * The `max` here is a convenience, not the rule: the server refuses a future date regardless, and it
 * is the server's refusal that carries the reason.
 *
 * THE FEE IS OPTIONAL AND MEANS THE SAME THING ON BOTH SIDES: added to the cost on a buy, taken out
 * of the proceeds on a sell. Either way the recorded total is what moved through the bank account.
 */

const today = (): string => new Date().toISOString().slice(0, 10);

export interface TransactionFormProps {
  open: boolean;
  onClose: () => void;
  editing: TransactionDto | null;
  onSubmit: (input: {
    kind: "buy" | "sell";
    shares: string;
    pricePerShare: string;
    fee: string;
    date: string;
  }) => void;
  pending: boolean;
  error?: string | null;
}

export function TransactionForm({
  open,
  onClose,
  editing,
  onSubmit,
  pending,
  error,
}: TransactionFormProps): ReactNode {
  const t = useT();
  const [kind, setKind] = useState<"buy" | "sell">(editing?.kind ?? "buy");
  const [shares, setShares] = useState(editing?.shares ?? "");
  const [price, setPrice] = useState(editing?.pricePerShare ?? "");
  const [fee, setFee] = useState(editing?.fee ?? "");
  const [date, setDate] = useState(editing?.date ?? today());

  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit({
      kind,
      shares,
      pricePerShare: price,
      fee: fee.trim() === "" ? "0" : fee,
      date,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t("inv.txn.editHeading") : t("inv.txn.recordHeading")}
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {/* The kind is fixed once recorded: a buy is not a sell that went the other way. */}
        {!editing && (
          <div>
            <Label htmlFor="txn-kind">{t("inv.txn.type")}</Label>
            <SelectField
              id="txn-kind"
              value={kind}
              onChange={(event) => {
                setKind(event.target.value as "buy" | "sell");
              }}
            >
              <option value="buy">{t("inv.txn.buy")}</option>
              <option value="sell">{t("inv.txn.sell")}</option>
            </SelectField>
          </div>
        )}

        <div>
          <Label htmlFor="txn-shares">{t("inv.table.shares")}</Label>
          <DecimalInput
            id="txn-shares"
            scale={4}
            value={shares}
            onValueChange={(next) => {
              setShares(next);
            }}
            placeholder="10"
            required
          />
          <p className="text-muted mt-1 text-xs">{t("inv.form.sharesHint")}</p>
        </div>

        <div>
          <Label htmlFor="txn-price">{t("inv.price.heading")}</Label>
          <DecimalInput
            id="txn-price"
            scale={4}
            value={price}
            onValueChange={(next) => {
              setPrice(next);
            }}
            placeholder="600.00"
            required
          />
        </div>

        <div>
          <Label htmlFor="txn-fee">{t("inv.form.fee")}</Label>
          <MoneyInput
            id="txn-fee"
            value={fee}
            onValueChange={(next) => {
              setFee(next);
            }}
            placeholder="2.50"
          />
        </div>

        <div>
          <Label htmlFor="txn-date">{t("inv.detail.date")}</Label>
          <TextField
            id="txn-date"
            type="date"
            value={date}
            max={today()}
            onChange={(event) => {
              setDate(event.target.value);
            }}
            required
          />
        </div>

        {error && <p className="text-danger text-sm">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={pending}>
            {editing ? t("common.save") : t("inv.txn.record")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
